import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import {
  ragLabel,
  ragPillClass,
  formatLakh,
  districtHealthCopy,
  buildDistrictInsightCards,
  buildWatchlist,
  buildDomainCards,
  buildActionCards,
  blockStatusTag,
  filterAndSortBlocks,
  exportBlocksCsv,
} from "@/utils/districtDetailDerived";
import { toTitleCase } from "@/utils/toTitleCase";
import DistrictHeroWarningModal from "@/components/DistrictHeroWarningModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./DistrictDetail.css";

const API = `${getBackendUrl()}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: "chart" },
  { id: "insights", label: "AI Insights", icon: "ai" },
  { id: "performance", label: "OSH Performance", icon: "performance" },
  { id: "domains", label: "Identity & Coverage", icon: "shield" },
  { id: "table", label: "All Blocks", icon: "table" },
  { id: "actions", label: "Actions", icon: "action" },
];

const TabIcon = ({ type }) => {
  if (type === "chart") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="M7 16v-5" />
        <path d="M12 16V7" />
        <path d="M17 16v-3" />
      </svg>
    );
  }
  if (type === "ai") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4" />
        <path d="M12 18v4" />
        <path d="m4.93 4.93 2.83 2.83" />
        <path d="m16.24 16.24 2.83 2.83" />
        <path d="M2 12h4" />
        <path d="M18 12h4" />
        <path d="m4.93 19.07 2.83-2.83" />
        <path d="m16.24 7.76 2.83-2.83" />
      </svg>
    );
  }
  if (type === "performance") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    );
  }
  if (type === "shield") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      </svg>
    );
  }
  if (type === "table") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16v16H4z" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 4h14v16H5z" />
      <path d="M9 2h6v4H9z" />
      <path d="M9 11l2 2 4-4" />
    </svg>
  );
};

const DomainIcon = ({ icon }) => {
  if (icon === "users") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
        <circle cx="12" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      </svg>
    );
  }
  if (icon === "shield") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  if (icon === "clipboard") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 4h14v16H5z" />
        <path d="M9 2h6v4H9z" />
        <path d="M9 11l2 2 4-4" />
      </svg>
    );
  }
  return (
    <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
    </svg>
  );
};

const WatchIcon = ({ variant }) => {
  if (variant === "orange") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }
  if (variant === "green") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4v18" />
        <path d="M19 21V11l-6-4" />
      </svg>
    );
  }
  return (
    <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
};

const ActionIcon = ({ variant }) => {
  if (variant === "orange") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }
  if (variant === "purple") {
    return (
      <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  return (
    <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  );
};

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const titleCase = (name) => {
  if (!name) return "";
  const s = String(name);
  return s.charAt(0) + s.slice(1).toLowerCase();
};

const gaugeStyle = (shi, tone) => {
  const pct = Math.min(Math.max(shi, 0), 100);
  const color = tone === "good" ? "#10b981" : tone === "amber" ? "#f59e0b" : "#ef4444";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${pct}%, rgba(16,185,129,.13) ${pct}% 100%)`,
  };
};

const iconBoxClass = (icon) => {
  if (icon === "users") return "green";
  if (icon === "shield") return "purple";
  if (icon === "clipboard") return "orange";
  return "";
};

const DistrictDetail = () => {
  const { districtCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [district, setDistrict] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("osh");
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [heroWarningId, setHeroWarningId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [districtRes, blocksRes] = await Promise.all([
        axios.get(`${API}/districts/${districtCode}`),
        axios.get(`${API}/districts/${districtCode}/blocks`),
      ]);
      setDistrict(districtRes.data);
      setBlocks(blocksRes.data || []);
    } catch (error) {
      console.error("Error fetching district data:", error);
      setDistrict(null);
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setActiveTab("overview");
    setStatusFilter("all");
    setSortKey("osh");
  }, [districtCode]);

  const health = useMemo(() => (district ? districtHealthCopy(district, blocks) : null), [district, blocks]);
  const insightCards = useMemo(() => (district ? buildDistrictInsightCards(district, blocks) : []), [district, blocks]);
  const watchlist = useMemo(() => buildWatchlist(blocks), [blocks]);
  const domainCards = useMemo(() => (district ? buildDomainCards(district, blocks) : []), [district, blocks]);
  const actionCards = useMemo(() => buildActionCards(blocks), [blocks]);

  const rankedBlocks = useMemo(
    () => [...blocks].sort((a, b) => (b.shi_score ?? 0) - (a.shi_score ?? 0)),
    [blocks],
  );

  const tableRows = useMemo(() => {
    const filter = statusFilter === "excellent" ? "excellent" : statusFilter === "at-risk" ? "at-risk" : "all";
    const sort =
      sortKey === "students" ? "students" : sortKey === "aadhaar" ? "aadhaar" : sortKey === "apaar" ? "apaar" : "osh";
    return filterAndSortBlocks(blocks, { statusFilter: filter, sortKey: sort });
  }, [blocks, statusFilter, sortKey]);

  const atRiskCount = useMemo(() => blocks.filter((b) => b.rag_status !== "green").length, [blocks]);

  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    document.getElementById(`dlv-${tabId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const refreshAll = async () => {
    await fetchData();
    toast.success("District data refreshed");
  };

  const generateInsights = async () => {
    setInsightsLoading(true);
    try {
      await axios.get(`${API}/analytics/insights/executive-summary`, {
        params: { district_code: districtCode },
      });
      toast.success("Insights generated");
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || "Failed to generate insights";
      if (status === 401) navigate("/login");
      else toast.error(detail);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleExportBlocks = () => exportBlocksCsv(tableRows);

  if (loading) {
    return (
      <div className="district-level-page">
        <div className="dlv-loading-wrap">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!district) {
    return (
      <div className="district-level-page">
        <div className="dlv-not-found">
          <p>District not found</p>
          <button type="button" className="dlv-primary-action" onClick={() => navigate("/")}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const pillClass = ragPillClass(district.rag_status);
  const ringClass = pillClass === "good" ? "good" : pillClass === "amber" ? "amber" : "critical";
  const eyebrowClass = pillClass === "good" ? "excellent" : pillClass === "amber" ? "amber" : "critical";
  const shi = district.shi_score ?? 0;
  const districtScopeLabel = `${district.district_name} District`;

  const openHeroWarning = (warningId) => setHeroWarningId(warningId);
  const handleHeroWarningKeyDown = (event, warningId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openHeroWarning(warningId);
    }
  };

  return (
    <div className="district-level-page" data-testid="district-detail">
      <section className="dlv-page-header" id="dlv-overview">
        <div>
          <div className={`dlv-eyebrow ${eyebrowClass}`}>
            <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {ragLabel(district.rag_status)} · District block view
          </div>
          <h1>{district.district_name}</h1>
          <p>
            Block-level monitoring for OSH performance, student identity readiness, school coverage and action
            prioritisation across {blocks.length} blocks.
          </p>
          <div className="dlv-meta-row">
            <span>
              <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              District Code: {district.district_code}
            </span>
            <span>
              <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              {blocks.length} Blocks
            </span>
            <span>
              <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Updated Today
            </span>
          </div>
        </div>
        <div className="dlv-header-actions">
          <button type="button" className="dlv-secondary-action" onClick={handleExportBlocks}>
            <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            Export
          </button>
          <button type="button" className="dlv-primary-action" onClick={refreshAll}>
            <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <div className="dlv-tabs-wrap" aria-label="District sections">
        <nav className="dlv-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`dlv-tab${activeTab === tab.id ? " active" : ""}`}
              onClick={() => scrollToSection(tab.id)}
            >
              <TabIcon type={tab.icon} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <section className="dlv-hero-card">
        <div className="dlv-hero-layout">
          <div>
            <div className="dlv-health-main">
              <div className={`dlv-gauge ${ringClass}`} style={gaugeStyle(shi, ringClass)} aria-label={`OSH score ${shi}`}>
                <div>
                  <strong>{shi.toFixed(1)}</strong>
                  <span>OSH Score</span>
                </div>
              </div>
              <div className="dlv-health-copy">
                <h2>{health?.title}</h2>
                <p>{health?.body}</p>
                <div className="dlv-status-row">
                  <HeroStatusChip
                    chipClass="dlv-status-chip"
                    variant={pillClass === "good" ? "green" : pillClass === "amber" ? "orange" : "red"}
                    onClick={
                      pillClass !== "good"
                        ? () => (atRiskCount > 0 ? openHeroWarning("blocks_at_risk") : document.getElementById("dlv-table")?.scrollIntoView({ behavior: "smooth", block: "start" }))
                        : undefined
                    }
                    ariaLabel={
                      pillClass !== "good"
                        ? `${ragLabel(district.rag_status)} status — view at-risk blocks`
                        : undefined
                    }
                  >
                    {ragLabel(district.rag_status)} Status
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="dlv-status-chip"
                    variant="blue"
                    onClick={() => document.getElementById("dlv-table")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    ariaLabel={`${blocks.length} blocks monitored — view block performance table`}
                  >
                    {blocks.length} Blocks Monitored
                  </HeroStatusChip>
                  {atRiskCount > 0 && (
                    <HeroStatusChip
                      chipClass="dlv-status-chip"
                      variant="orange"
                      onClick={() => openHeroWarning("blocks_at_risk")}
                      ariaLabel={`${atRiskCount} blocks at risk — open drill-down`}
                    >
                      {atRiskCount} Block{atRiskCount !== 1 ? "s" : ""} At Risk
                    </HeroStatusChip>
                  )}
                </div>
              </div>
            </div>
            <div className="dlv-summary-note">
              <div className="dlv-icon-box green">
                <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <div>
                <b>Primary district action</b>
                <span>{health?.action}</span>
              </div>
            </div>
          </div>

          <div className="dlv-hero-metrics" aria-label="District KPIs">
            <article className="dlv-metric-card">
              <small>Total Schools</small>
              <strong>{fmt(district.total_schools)}</strong>
              <span>{blocks.length} blocks covered</span>
            </article>
            <article className="dlv-metric-card">
              <small>Total Students</small>
              <strong>{fmt(district.total_students)}</strong>
              <span>Enrolment base</span>
            </article>
            <article className="dlv-metric-card">
              <small>Aadhaar Compliance</small>
              <strong>{(district.aadhaar_percentage ?? 0).toFixed(1)}%</strong>
              <span>Strong identity baseline</span>
            </article>
            <article
              className="dlv-metric-card warning clickable"
              role="button"
              tabIndex={0}
              aria-label="APAAR coverage — open lower blocks drill-down"
              onClick={() => openHeroWarning("apaar_lower_blocks")}
              onKeyDown={(e) => handleHeroWarningKeyDown(e, "apaar_lower_blocks")}
            >
              <small>APAAR Coverage</small>
              <strong>{(district.apaar_percentage ?? 0).toFixed(1)}%</strong>
              <span>Focus on lower blocks</span>
            </article>
          </div>
        </div>
      </section>

      <section className="dlv-insight-panel" id="dlv-insights">
        <div className="dlv-insight-head">
          <div>
            <h3>
              <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4" />
                <path d="M12 18v4" />
                <path d="m4.93 4.93 2.83 2.83" />
                <path d="m16.24 16.24 2.83 2.83" />
                <path d="M2 12h4" />
                <path d="M18 12h4" />
              </svg>
              AI Insights for district block monitoring
            </h3>
            <p>Converted from a long scrollable text block into concise insight cards so officers can scan, understand and act quickly.</p>
          </div>
          <button type="button" className="dlv-primary-action" onClick={generateInsights} disabled={insightsLoading}>
            {insightsLoading ? (
              <svg className="dlv-svg-icon dlv-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : (
              <svg className="dlv-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4" />
                <path d="M12 18v4" />
                <path d="M2 12h4" />
                <path d="M18 12h4" />
              </svg>
            )}
            Generate Insights
          </button>
        </div>
        <div className="dlv-insight-grid">
          {insightCards.map((card) => (
            <article className="dlv-insight-card" key={card.label}>
              <span className="dlv-insight-label">{card.label}</span>
              <b>{card.title}</b>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="dlv-section-title" id="dlv-performance">
        <div>
          <h2>Block-Wise OSH Performance</h2>
          <p>
            Performance chart now uses a compact ranking pattern with a separate watchlist, avoiding the oversized single
            chart and improving decision clarity.
          </p>
        </div>
        <button type="button" className="dlv-section-action" onClick={() => scrollToSection("table")}>
          Open all blocks →
        </button>
      </div>

      <section className="dlv-analytics-grid">
        <article className="dlv-panel">
          <div className="dlv-panel-head">
            <div>
              <h3>Top and Bottom Block Ranking</h3>
              <p>Quick comparison of the leading blocks and the lowest scoring blocks.</p>
            </div>
            <span className="dlv-pill green">OSH Score</span>
          </div>
          <div className="dlv-bar-list" aria-label="Block OSH horizontal ranking chart">
            {rankedBlocks.map((block) => {
              const score = block.shi_score ?? 0;
              const isLow = block.rag_status !== "green";
              return (
                <div className="dlv-hbar-row" key={block.block_code}>
                  <div className="dlv-hbar-label">{titleCase(block.block_name)}</div>
                  <div className="dlv-hbar-track">
                    <div className={`dlv-hbar${isLow ? " orange" : ""}`} style={{ width: `${Math.min(score, 100)}%` }} />
                  </div>
                  <div className="dlv-hbar-value">{score.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="dlv-panel">
          <div className="dlv-panel-head">
            <div>
              <h3>Watchlist and Spread</h3>
              <p>Blocks needing officer attention before the next review cycle.</p>
            </div>
            <span className="dlv-pill orange">Priority</span>
          </div>
          <div className="dlv-watch-list">
            {watchlist.items.map((item) => (
              <div className="dlv-watch-item" key={item.block.block_code}>
                <div className={`dlv-icon-box ${item.variant}`}>
                  <WatchIcon variant={item.variant} />
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <div className="dlv-watch-score">{item.score.toFixed(1)}</div>
              </div>
            ))}
          </div>
          <div className="dlv-score-grid">
            <div className="dlv-score-tile">
              <small>Highest OSH</small>
              <strong>{watchlist.highest.toFixed(1)}</strong>
            </div>
            <div className="dlv-score-tile">
              <small>Lowest OSH</small>
              <strong>{watchlist.lowest.toFixed(1)}</strong>
            </div>
            <div className="dlv-score-tile">
              <small>Spread</small>
              <strong>{watchlist.spread.toFixed(1)}</strong>
            </div>
          </div>
        </article>
      </section>

      <div className="dlv-section-title" id="dlv-domains">
        <div>
          <h2>Identity and Coverage Snapshot</h2>
          <p>Four diagnostic cards make it easier to understand where the district is strong and where the block-level gaps remain.</p>
        </div>
        <button type="button" className="dlv-section-action" onClick={() => scrollToSection("actions")}>
          Go to actions →
        </button>
      </div>

      <section className="dlv-domain-grid">
        {domainCards.map((card) => (
          <article className="dlv-domain-card" key={card.title}>
            <div className="dlv-domain-head">
              <div className={`dlv-icon-box ${iconBoxClass(card.icon)}`}>
                <DomainIcon icon={card.icon} />
              </div>
              <span className={`dlv-status-dot${card.dot === "amber" ? " amber" : ""}`} />
            </div>
            <h3>{toTitleCase(card.title)}</h3>
            <div className="dlv-domain-score">
              <strong>{card.score}</strong>
              <span>{card.unit}</span>
            </div>
            <div className="dlv-progress-line">
              <i className={card.progressColor || undefined} style={{ width: `${Math.min(card.progress, 100)}%` }} />
            </div>
            <div className="dlv-domain-meta">
              <span>{card.metaLeft}</span>
              <span>{card.metaRight}</span>
            </div>
          </article>
        ))}
      </section>

      <section className="dlv-panel dlv-table-card" id="dlv-table">
        <div className="dlv-table-head">
          <div>
            <h3>All Blocks ({blocks.length})</h3>
            <p>Clean tabular view with inline OSH bars, status chips and enough row spacing for comfortable scanning.</p>
          </div>
          <div className="dlv-table-actions">
            <label className="dlv-mini-filter">
              Status:
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
                <option value="all">All</option>
                <option value="excellent">Excellent</option>
                <option value="at-risk">At Risk</option>
              </select>
            </label>
            <label className="dlv-mini-filter">
              Sort:
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} aria-label="Sort blocks">
                <option value="osh">OSH score</option>
                <option value="students">Students</option>
                <option value="aadhaar">Aadhaar %</option>
                <option value="apaar">APAAR %</option>
              </select>
            </label>
            <button type="button" className="dlv-mini-filter" onClick={handleExportBlocks}>
              Download CSV
            </button>
          </div>
        </div>
        <div className="dlv-table-wrap">
          <table aria-label="All block performance table">
            <thead>
              <tr>
                <th>Block Name</th>
                <th>Schools</th>
                <th>Students</th>
                <th>Aadhaar %</th>
                <th>APAAR %</th>
                <th>OSH Score</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, idx) => {
                const status = blockStatusTag(row.rag_status);
                const score = row.shi_score ?? 0;
                const barOrange = row.rag_status !== "green";
                return (
                  <tr key={row.block_code} onClick={() => navigate(`/block/${row.block_code}`)}>
                    <td>
                      <span className="dlv-block-name">
                        <span className="dlv-rank-dot">{idx + 1}</span>
                        {titleCase(row.block_name)}
                      </span>
                    </td>
                    <td>{fmt(row.total_schools)}</td>
                    <td>{fmt(row.total_students)}</td>
                    <td>{(row.aadhaar_percentage ?? 0).toFixed(1)}%</td>
                    <td>{(row.apaar_percentage ?? 0).toFixed(1)}%</td>
                    <td>
                      <div className="dlv-mini-progress">
                        <span className="dlv-mini-track">
                          <i className={barOrange ? "orange" : ""} style={{ width: `${Math.min(score, 100)}%` }} />
                        </span>
                        <b>{score.toFixed(1)}</b>
                      </div>
                    </td>
                    <td>
                      <span className={`dlv-tag${status.className ? ` ${status.className}` : ""}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="dlv-section-title" id="dlv-actions">
        <div>
          <h2>Priority Action Queue</h2>
          <p>Action cards make the page operational, not just analytical. Each item has a clear owner-style focus and timeline.</p>
        </div>
        <button type="button" className="dlv-section-action" onClick={() => scrollToSection("overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="dlv-action-grid">
        {actionCards.map((card) => (
          <article className="dlv-action-card" key={card.title}>
            <div className={`dlv-icon-box ${card.variant}`}>
              <ActionIcon variant={card.variant} />
            </div>
            <h3>{toTitleCase(card.title)}</h3>
            <p>{card.body}</p>
            <span className="dlv-due">{card.due}</span>
          </article>
        ))}
      </section>

      <DistrictHeroWarningModal
        open={Boolean(heroWarningId)}
        warningId={heroWarningId}
        onClose={() => setHeroWarningId(null)}
        district={district}
        blocks={blocks}
        scopeLabel={districtScopeLabel}
      />
    </div>
  );
};

export default DistrictDetail;
